/**
 * Role-aware URL prefix helpers (DR + Auth Enhancements P7, 2026-05-24).
 *
 * The Manager mounts the same component tree under three URL prefixes:
 *   /admin/*    — role=admin
 *   /partner/*  — role=partner
 *   /portal/*   — role=user
 *
 * These helpers compute the right prefix from the current Principal so
 * pages can build links without hardcoding /admin/ everywhere.
 */
import { useCallback } from 'react';
import { useAuth, type Role } from '@/contexts/AuthContext';

export type RolePrefix = 'admin' | 'partner' | 'portal';

export function prefixFor(role: Role | null | undefined): RolePrefix {
  if (role === 'admin') return 'admin';
  if (role === 'partner') return 'partner';
  // 'user' → /portal, also the safe default when role isn't known yet.
  return 'portal';
}

export function landingFor(role: Role | null | undefined): string {
  return `/${prefixFor(role)}/dashboard`;
}

/**
 * Hook returning a (relative -> absolute) URL builder pinned to the
 * current user's role prefix. Example:
 *   const rp = useRolePath();
 *   <Link to={rp('/data-room')} />   // → "/partner/data-room" for partners
 *
 * Pass a leading slash on the relative path; the helper normalises.
 */
export function useRolePath(): (relative: string) => string {
  const { user } = useAuth();
  const prefix = prefixFor(user?.role);
  return useCallback(
    (relative: string) => {
      const r = relative.startsWith('/') ? relative : `/${relative}`;
      return `/${prefix}${r}`;
    },
    [prefix],
  );
}
